import axios from "axios";
import { CityKey } from "@/features/services";
import { withRetry } from "@/api/retry";

/**
 * kef-compare («Кэф vs Доход»). В dev — через прокси dev-сервера, который
 * подставляет куку сам; в проде — напрямую, токен заголовком.
 */
const $kefc = axios.create({
  baseURL: __KEFC_URL__,
  timeout: 20000,
});

$kefc.interceptors.request.use((config) => {
  if (__KEFC_TOKEN__) {
    config.headers.Authorization = `Bearer ${__KEFC_TOKEN__}`;
  }
  return config;
});

export interface ForecastHour {
  /** ISO, UTC — начало часа. */
  h: string;
  /** Горизонт от снимка: ≤ 0 — уже идущий/прошедший час (live), > 0 — прогноз. */
  horizon: number;
  v: number;
  src: "live" | "fore";
}

export interface ForecastTariff {
  tariff: number;
  name: string;
  kef: string;
  days: number;
  quality: { mape: number | null; n_train: number | null; shrink_k0: number | null };
  hours: ForecastHour[];
}

export interface ForecastCity {
  tz: number;
  tariffs: ForecastTariff[];
}

export interface ForecastResponse {
  formula: string;
  made_hour: string | null;
  denominator?: string;
  as_of?: { orders_to?: string; online_to?: string };
  best_next_hour?: {
    target_hour: string;
    rows: { city: string; tariff: number; name: string; v: number }[];
  } | null;
  cities: Partial<Record<CityKey, ForecastCity>>;
  note?: string;
}

/**
 * Почасовой ряд «доход в час» — позиционные строки, поля читать по `fields`.
 * Здесь нужен только `orders`: он доезжает за минуты, включая текущий час,
 * тогда как деньги и часы онлайна дозревают до двух суток.
 */
export interface IncomeHourlyResponse {
  days: number;
  /** Эпоха-час первой строки; `h` в строках — смещение от него. */
  h0: number;
  n: number;
  cities: string[];
  tariffs: number[];
  fields: string[];
  rows: (number | null)[][];
}

export function getIncomeHourly(days: number): Promise<IncomeHourlyResponse> {
  // Ручка считает неделю по своей базе — 7–15 с, после выката дольше.
  return withRetry(async () => {
    const { data } = await $kefc.get<IncomeHourlyResponse>("/income_hourly", {
      params: { days },
      timeout: 60000,
    });
    return data;
  });
}

export function getForecast(city: CityKey): Promise<ForecastResponse> {
  return withRetry(async () => {
    const { data } = await $kefc.get<ForecastResponse>("/forecast", {
      params: { city },
    });
    return data;
  });
}
